import { useState, useMemo } from "react";
import { useTransition, animated } from "@react-spring/web";
import { X, Search, ShieldCheck } from "lucide-react";
import { useOrg360FieldSecurity } from "@/hooks/org360/useOrg360FieldSecurity";

interface FieldSecurityOverlayProps {
  open: boolean;
  objectName: string | null;   // null = open but no object selected yet
  onClose: () => void;
}

const FIELD_COL_W = 180;  // px — sticky left column
const CELL_W_MIN  = 32;   // px — minimum profile column width (expands to fill space)
const CELL_H      = 28;   // px — row height

const EDIT_COLOR = "#22c55e";
const READ_COLOR = "#3b82f6";

export function FieldSecurityOverlay({ open, objectName, onClose }: FieldSecurityOverlayProps) {
  const [fieldSearch, setFieldSearch] = useState("");

  // useTransition keeps the overlay in the DOM until the leave animation finishes
  const transition = useTransition(open, {
    from:  { opacity: 0, y: 24 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: 24 },
    config: { tension: 320, friction: 28 },
  });

  const { data, isLoading, error } = useOrg360FieldSecurity(objectName);

  const filteredFields = useMemo(() => {
    if (!data) return [];
    if (!fieldSearch.trim()) return data.fields;
    const q = fieldSearch.toLowerCase();
    return data.fields.filter((f) => f.toLowerCase().includes(q));
  }, [data, fieldSearch]);

  return transition((style, isOpen) => isOpen ? (
    <animated.div
      className="absolute inset-0 z-40 flex flex-col bg-card"
      style={{ opacity: style.opacity, transform: style.y.to((y) => `translateY(${y}px)`) }}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-border flex-shrink-0 flex-wrap">
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <div>
            <h2 className="font-display font-bold text-[17px] leading-tight">
              Field Security{objectName ? ` · ${objectName}` : ""}
            </h2>
            {data && (
              <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
                {data.fields.length} fields × {data.principals.length} profiles / permission sets
                {(data.truncated.fields || data.truncated.principals) && (
                  <span className="ml-2 text-amber-500">· truncated</span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Legend */}
        {data && (
          <div className="flex items-center gap-4 flex-shrink-0">
            {[
              { color: EDIT_COLOR,              label: "Edit", border: false },
              { color: READ_COLOR,              label: "Read", border: false },
              { color: "hsl(var(--border))",    label: "None", border: true  },
            ].map(({ color, label, border }) => (
              <span key={label} className="flex items-center gap-1.5 font-mono text-[12px] text-muted-foreground">
                <span
                  className="w-3.5 h-3.5 rounded-sm flex-shrink-0"
                  style={{
                    background: border ? "transparent" : color,
                    border: border ? `1.5px solid hsl(var(--border))` : undefined,
                  }}
                />
                {label}
              </span>
            ))}
          </div>
        )}

        {/* Field search */}
        <div className="flex items-center gap-2 px-3 h-9 rounded-lg border border-border bg-muted/50 flex-1 min-w-[180px] max-w-xs">
          <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <input
            type="text"
            value={fieldSearch}
            onChange={(e) => setFieldSearch(e.target.value)}
            placeholder="Filter fields…"
            className="flex-1 bg-transparent text-[13px] font-mono text-foreground
              placeholder:text-muted-foreground outline-none min-w-0"
          />
          {fieldSearch && (
            <button type="button" onClick={() => setFieldSearch("")}
              className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="ml-auto flex-shrink-0 grid place-items-center w-8 h-8 rounded-full
            text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
          aria-label="Close field security"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ── Body ── */}

      {/* No object selected yet */}
      {!objectName && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-8">
          <div className="grid place-items-center w-16 h-16 rounded-full bg-muted">
            <ShieldCheck className="h-7 w-7 text-muted-foreground/50" />
          </div>
          <div>
            <p className="font-display font-semibold text-[15px] text-foreground">
              Select an object to inspect
            </p>
            <p className="font-mono text-[12px] text-muted-foreground mt-1.5 leading-relaxed max-w-xs">
              Click a <span className="text-foreground font-semibold">CustomObject</span> or{" "}
              <span className="text-foreground font-semibold">StandardObject</span> node in the graph,
              or browse by type in the left panel.
            </p>
          </div>
        </div>
      )}

      {objectName && isLoading && (
        <div className="flex-1 flex flex-col gap-3 p-8">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-4 rounded bg-muted animate-pulse" style={{ width: `${FIELD_COL_W}px` }} />
              <div className="flex gap-1">
                {Array.from({ length: 12 }).map((_, j) => (
                  <div key={j} className="rounded-sm bg-muted animate-pulse"
                    style={{ width: CELL_W_MIN, height: CELL_H }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {objectName && (error || (!isLoading && !data)) && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center">
          <ShieldCheck className="h-10 w-10 text-muted-foreground/20" />
          <p className="text-[14px] text-muted-foreground font-mono">
            Could not load field security data for {objectName}.
          </p>
        </div>
      )}

      {/* No explicit FLS configured for this object */}
      {data && data.principals.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-8">
          <div className="grid place-items-center w-16 h-16 rounded-full bg-muted">
            <ShieldCheck className="h-7 w-7 text-muted-foreground/40" />
          </div>
          <div>
            <p className="font-display font-semibold text-[15px] text-foreground">
              No field-level security configured
            </p>
            <p className="font-mono text-[12px] text-muted-foreground mt-1.5 leading-relaxed max-w-sm">
              <span className="font-semibold text-foreground">{objectName}</span> has{" "}
              {data.fields.length} fields but no explicit profile or permission set restrictions.
              All fields follow object-level access — anyone with access to the object can read/edit all fields.
            </p>
          </div>
        </div>
      )}

      {data && data.principals.length > 0 && (
        <div className="flex-1 overflow-auto min-h-0">
          {/* width:100% lets columns expand to fill space when few profiles */}
          <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%", minWidth: FIELD_COL_W + data.principals.length * CELL_W_MIN }}>
            <thead>
              <tr>
                {/* Corner label */}
                <th
                  className="sticky left-0 z-10 bg-card p-0 align-bottom border-b border-r border-border"
                  style={{ width: FIELD_COL_W, minWidth: FIELD_COL_W }}
                >
                  <div
                    className="font-mono text-[11px] text-muted-foreground px-4 pb-3"
                    style={{ height: 140 }}
                  >
                    Field \ Profile / Perm Set
                  </div>
                </th>

                {/* Rotated profile headers */}
                {data.principals.map((p) => (
                  <th
                    key={p.name}
                    className="p-0 align-bottom border-b border-l border-border"
                    style={{ minWidth: CELL_W_MIN }}
                    title={p.name}
                  >
                    <div
                      className="font-mono text-[11px] text-muted-foreground"
                      style={{
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                        height: 140,
                        padding: "8px 6px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredFields.map((field) => {
                const fieldAccess = data.grid[field] ?? {};
                const hasExplicitFLS = Object.keys(fieldAccess).length > 0;
                return (
                  <tr key={field} className="group hover:bg-muted/20 transition-colors duration-75">
                    <td
                      className="sticky left-0 z-10 bg-card group-hover:bg-muted/20 border-r border-border px-4"
                      style={{ width: FIELD_COL_W, minWidth: FIELD_COL_W, height: CELL_H }}
                    >
                      <span
                        className="font-mono text-[12px] text-foreground truncate block"
                        title={field}
                      >
                        {field}
                      </span>
                    </td>

                    {data.principals.map((p) => {
                      const access = fieldAccess[p.name];
                      // Always show a faint cell so the grid reads as a grid
                      const bg =
                        access === "edit" ? EDIT_COLOR
                        : access === "read" ? READ_COLOR
                        : "hsl(var(--muted))";
                      const opacity =
                        access === "read" ? 0.55
                        : !access ? 0.35   // faint but visible grid cell
                        : 1;
                      return (
                        <td
                          key={p.name}
                          className="border-l border-border/40"
                          style={{ minWidth: CELL_W_MIN, height: CELL_H, padding: "4px 5px" }}
                          title={`${field} · ${p.name}: ${access ?? (hasExplicitFLS ? "none" : "system field")}`}
                        >
                          <div
                            style={{
                              background: bg,
                              opacity,
                              height: "100%",
                              borderRadius: 3,
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </animated.div>
  ) : null);
}
